import Edge from './edge';
import Prediction from './prediction';
import { toWinRate } from './utils';
import SgfUtil from './sgf/sgfUtil';
import BoardModel from './boardModel';

class Node {
  constructor(key, boardModel, priorProbability, evaluation) {
    /**
     * Prediction key
     */
    this.key = key;
    /**
     * Board model
     */
    this.boardModel = boardModel;
    /**
     * Prior probability (policy output)
     */
    this.priorProbability = priorProbability;
    /**
     * Evaluation of this state (value output)
     */
    this.evaluation = evaluation;
    /**
     * Children nodes - {moveIndex: childNode}
     */
    this.children = {};
    /**
     * Visit count
     */
    this.visitCount = 1;
    /**
     * Edges for all actions
     */
    this.edges = [];
    /**
     * Level of exploration
     */
    this.c = 1.0;
    this.initialize();
  }

  initialize() {
    this.priorProbability.filter((_, index) => index <= this.boardSize ** 2)
      .forEach((pp, index) => {
        let illegal = false;
        if (index === this.boardSize ** 2) { // pass
          illegal = true;
        } else {
          const vertex = SgfUtil.indexToVertex(index, this.boardSize);
          illegal = (this.boardModel.getState(vertex[0], vertex[1]) !== BoardModel.EMPTY);
        }
        this.edges.push(new Edge(pp, index, illegal));
      });
  }

  get boardSize() {
    return this.boardModel.boardSize;
  }

  get lastMove() {
    const v = this.boardModel.lastMove
    if (!v) {
      return v;
    }
    return v[0] - 1 + (v[1] - 1) * this.boardModel.boardSize;
  }

  get meanEvaluation() {
    return this.visitCount
      ? this.evaluation / this.visitCount
      : 0;
  }

  /**
   * Searches leaf node and selects the next move.
   */
  async searchLeaf(priority) {
    let node = this;
    let nextMove = node.selectNextMove(priority);
    const sequence = [node];
    while (nextMove !== null && node.edges.length && node.hasChild(nextMove)) {
      const child = node.children[nextMove];
      node = child;
      nextMove = node.selectNextMove(0);
      sequence.push(node);
    }
    return [node, nextMove, sequence];
  }

  /**
   * Selects next move according to score: Q(s,a) + U(s,a).
   * 
   * @param {*} priority thread priority
   * @returns next move index or null if no move to select
   */
  selectNextMove(priority) {
    // exclude illegal and pass moves
    const arr = this.edges.filter(edge => !edge.illegal);    
    if (arr.length <= priority) {
      console.log("No more variations to select.");
      return null;
    }
    const meanEval = this.meanEvaluation;
    let bestEdge = arr.sort((a, b) => {
      return b.score(this.c, this.visitCount, meanEval) 
      - a.score(this.c, this.visitCount, meanEval);
    });
    if (priority > 0) {
      const e1 = bestEdge[0].priorProbability;
      const e2 = bestEdge[priority].priorProbability;
      if (e2 > e1 - 0.2) {
        bestEdge = bestEdge.slice(priority);
      } else {
        // console.log(`Variation score is too low. ${e1} -> ${e2}`)
        return null;
      }
    }
    for (const e of bestEdge) {
      let board = this.boardModel;
      const vertex = SgfUtil.indexToVertex(e.nextMove, this.boardSize);
      try {
        board = this.boardModel.clone();
        if (this.boardModel.blackIsNext) {
          board.moveBlack(vertex[0], vertex[1]);
        } else {
          board.moveWhite(vertex[0], vertex[1]);
        }
      } catch (error) {
        console.log("Illegal move: " + vertex + " " + this.boardModel.hash);
        e.illegal = true;
        continue;
      }
      return e.nextMove;
    }
    console.error("No more next move to select. " + this.key, this.edges);
    return null;
  }

  play(move) {
    const boardModel = this.boardModel.clone();
    boardModel.move(move);
    return boardModel;
  }

  isPass(boardSize, move) {
    return move === boardSize ** 2;
  }

  /**
   * Updates edge statistics in a backward pass.
   * 
   * @param {number} move
   * @param {*} sequence 
   * @param {number} value
   */
  backup(move, sequence, value) {
    if (sequence.length < 1) {
      return;
    }
    for (let i = sequence.length - 1; i >= 0; i--) {
      value *= -1;
      let node = sequence[i];
      node.visitCount++;
      node.evaluation += value;
      node.edges[move].visitCount++;
      node.edges[move].totalActionValue += value;
      move = node.lastMove;
    }
  }

  addChild(childNode) {
    this.children[childNode.lastMove] = childNode;
  }

  hasChild(move) {
    return this.children[move];
  }

  deleteChild(move) {
    delete this.children[move];
  }

  async getPrediction() {
    const prediction = new Prediction();
    prediction.node = this;
    prediction.key = this.key;
    prediction.priorProbability = this.priorProbability;
    prediction.visits = this.edges.map(edge => edge.visitCount);
    prediction.evaluations = this.edges.map(edge => edge.meanActionValue);
    const maxVisit = prediction.visits.reduce((a, b) => Math.max(a, b));
    const indices = [];
    prediction.visits.forEach((visit, index) => {
      if (visit === maxVisit) {
        indices.push(index);
      }
    });
    prediction.bestMoveIndex = this.selectBestMove();
    const bestEdge = this.edges[prediction.bestMoveIndex];
    prediction.valueOutput = bestEdge.visitCount ? bestEdge.meanActionValue
    : this.meanEvaluation;
    return prediction;
  }

  getBestSequence() {
    let node = this;
    let nextMove = node.selectBestMove();
    const sequence = [node];
    while (nextMove !== null
      && nextMove < this.boardModel.boardSize ** 2
      && node.hasChild(nextMove)) {
      const child = node.children[nextMove];
      node = child;
      nextMove = node.selectBestMove();
      sequence.push(node);
    }
    return sequence;
  }

  sortEdges() {
    const arr = this.edges.filter(edge => !edge.illegal);
    return arr.sort((a, b) => {
      if (a.visitCount !== b.visitCount) {
        return b.visitCount - a.visitCount;
      } else if (b.meanActionValue !== a.meanActionValue) {
        return b.meanActionValue - a.meanActionValue;
      } else {
        return b.priorProbability - a.priorProbability;
      }
    });
  }

  selectBestMove() {    
    const edges = this.sortEdges();
    return edges.length ? edges[0].nextMove : null;
  }

  // not used
  resetVisits() {
    this.visitCount = 0;
    this.edges.forEach(edge => {
      if (edge.visitCount) {
        edge.totalActionValue /= edge.visitCount;
        edge.visitCount = 1;
      }
    });
  }

  toString() {
    let vertex;
    if (this.boardModel.lastMove) {
      const v = this.boardModel.lastMove;
      vertex = `[${v[0]},${v[1]}]`;      
    } else {
      vertex = "null";
    }
    return `vertex=${vertex}, ` +
      `visits=${this.visitCount}, ` +
      `winRate=${toWinRate(this.meanEvaluation).toFixed(1)}%, ` +
      `c=${this.c}, ` +
      `children=${Object.keys(this.children).length} ` +
      `lastMove=${this.boardModel.lastMove}, boardSize=${this.boardSize}`;
  }
}

export default Node;